/**
 * Voucher Primitives — PDF-safe presentational building blocks shared by
 * all voucher body templates (Activity, Hotel, Flight, Transfer, …).
 *
 * Strict PDF-rendering rule: html2canvas / Playwright print pipelines do
 * NOT handle flexbox reliably (icons drift relative to baseline, gaps
 * render unpredictably). EVERY layout in this file uses block /
 * inline-block / table / table-cell / grid only — DO NOT introduce
 * display:flex or any className containing the word "flex" anywhere in
 * this file.
 *
 * Nothing here has any domain (Activity/Hotel/Flight) knowledge — these
 * are pure visual primitives so the editable wrappers in
 * VoucherEditComponents.js, and downstream consumers in
 * ActivityVoucherBody / HotelVoucherBody / FlightVoucherBody, can all
 * share the same look-and-feel.
 */
import React from 'react';

// ---------------------------------------------------------------------------
// Tiny value helpers
// ---------------------------------------------------------------------------

export const has = (v) => v !== undefined && v !== null && String(v).trim() !== '';

export const toArray = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) {
    return v.filter(Boolean).map((x) => String(x).trim()).filter(Boolean);
  }
  return String(v).split(/[\n;,•·●○]+/g).map((s) => s.trim()).filter(Boolean);
};

export const isHtmlLike = (s) =>
  typeof s === 'string' && /<\/?[a-z][\s\S]*>/i.test(s);

// ---------------------------------------------------------------------------
// InlineHtmlOrText — renders raw HTML safely if the value looks like HTML,
// otherwise a `whitespace-pre-line` <p>. Word-break + overflow-wrap are
// always on so long URLs/IDs don't break the PDF column.
// ---------------------------------------------------------------------------
export const InlineHtmlOrText = ({ value, className = '' }) => {
  if (!has(value)) return null;
  const wrap = {
    display: 'block',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    overflow: 'visible',
    maxWidth: '100%',
  };
  if (isHtmlLike(value)) {
    return (
      <div
        className={`prose prose-sm max-w-none ${className}`}
        style={wrap}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    );
  }
  return (
    <p className={`whitespace-pre-line leading-relaxed ${className}`} style={wrap}>
      {value}
    </p>
  );
};

// ---------------------------------------------------------------------------
// IconTextRow — pure CSS-table icon+text helper (PDF-safe). Used everywhere
// a lucide-react Icon needs to sit next to a label/value at baseline.
// ---------------------------------------------------------------------------
export const IconTextRow = ({ icon: Icon, children, iconStyle = {}, gap = '8px' }) => (
  <div style={{ display: 'table', width: '100%' }}>
    {Icon && (
      <div
        style={{
          display: 'table-cell',
          verticalAlign: 'middle',
          width: '14px',
          paddingRight: gap,
        }}
      >
        <Icon
          style={{ display: 'block', height: '14px', width: '14px', ...iconStyle }}
        />
      </div>
    )}
    <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
      {children}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// SectionCard — uniform card shell used by every section across all voucher
// templates. Page-break-safe; heading uses IconTextRow. `headerAction` is
// rendered to the right of the title (used by EditableArraySectionCard to
// host the pencil / Lock icon).
// ---------------------------------------------------------------------------
export const SectionCard = ({
  title,
  icon: Icon,
  headerAction,
  children,
  accent = '#595A5B',
  testId,
  className = '',
  bordered = true,
}) => (
  <section
    data-testid={testId}
    className={`${bordered ? 'card bg-slate-50 border border-slate-200' : ''} break-inside-avoid pdf-no-break ${className}`}
    style={{
      display: 'block',
      width: '100%',
      borderRadius: bordered ? '12px' : '0',
      padding: bordered ? '20px' : '0',
      marginTop: '0px',
      marginBottom: bordered ? '20px' : '12px',
      ...(bordered ? {} : {
        borderTop: '1px solid #E5E7EB',
        paddingTop: '14px',
        background: 'transparent',
        boxShadow: 'none',
      }),
      pageBreakInside: 'avoid',
      breakInside: 'avoid',
      pageBreakBefore: 'auto',
      breakBefore: 'auto',
      pageBreakAfter: 'auto',
      breakAfter: 'auto',
      overflow: 'visible',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      maxWidth: '100%',
      boxSizing: 'border-box',
    }}
  >
    {title && (
      <div
        style={{ display: 'table', width: '100%', marginBottom: '14px' }}
        className="pdf-no-break"
      >
        <h3
          className="uppercase font-semibold"
          style={{
            display: 'table-cell',
            verticalAlign: 'middle',
            fontSize: '11px',
            letterSpacing: '0.2em',
            margin: 0,
            color: accent,
            pageBreakAfter: 'avoid',
            breakAfter: 'avoid',
            lineHeight: 1,
          }}
        >
          <IconTextRow icon={Icon} iconStyle={{ opacity: 0.7 }}>
            {title}
          </IconTextRow>
        </h3>
        {headerAction && (
          <div
            style={{
              display: 'table-cell',
              verticalAlign: 'middle',
              textAlign: 'right',
              whiteSpace: 'nowrap',
              width: '1%',
            }}
          >
            {headerAction}
          </div>
        )}
      </div>
    )}
    <div
      className="text-sm text-slate-700"
      style={{
        display: 'block',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        maxWidth: '100%',
        overflow: 'visible',
      }}
    >
      {children}
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// InfoBadge — compact label/value chip for the quick-info strip at the top
// of voucher bodies. Inline-block (no flex anywhere).
// ---------------------------------------------------------------------------
export const InfoBadge = ({ label, value, accent = '#595A5B' }) => {
  if (!has(value)) return null;
  const v = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
  return (
    <div
      className="rounded-lg bg-white border border-slate-200"
      style={{
        display: 'inline-block',
        verticalAlign: 'top',
        padding: '8px 12px',
        marginRight: '8px',
        marginBottom: '8px',
        minWidth: '90px',
      }}
    >
      <span
        style={{
          display: 'block',
          fontSize: '9px',
          textTransform: 'uppercase',
          letterSpacing: '0.18em',
          fontWeight: 600,
          color: accent,
        }}
      >
        {label}
      </span>
      <span
        style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: 600,
          color: '#0f172a',
          marginTop: '2px',
          lineHeight: 1.2,
        }}
      >
        {v}
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// BulletList — each <li> is a CSS table (glyph cell + text cell). `kind`
// switches the glyph + colour: plain "•", include "✓" (green), exclude "✗"
// (rose).
// ---------------------------------------------------------------------------
export const BulletList = ({ items, kind = 'plain' }) => {
  if (!items || items.length === 0) return null;
  const glyph = kind === 'include' ? '✓' : kind === 'exclude' ? '✗' : '•';
  const glyphColor =
    kind === 'include' ? '#059669' : kind === 'exclude' ? '#f43f5e' : '#94a3b8';
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'block' }}>
      {items.map((it, i) => (
        <li
          key={i}
          style={{
            display: 'table',
            width: '100%',
            marginTop: '0px',
            marginBottom: '6px',
            pageBreakInside: 'avoid',
            breakInside: 'avoid',
          }}
        >
          <span
            style={{
              display: 'table-cell',
              verticalAlign: 'top',
              whiteSpace: 'nowrap',
              paddingRight: '8px',
              width: '14px',
              fontWeight: 700,
              color: glyphColor,
              fontSize: '14px',
              lineHeight: '1.55',
              textAlign: 'left',
            }}
          >
            {glyph}
          </span>
          <span
            style={{
              display: 'table-cell',
              verticalAlign: 'top',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              fontSize: '14px',
              lineHeight: '1.55',
              color: '#334155',
            }}
          >
            {it}
          </span>
        </li>
      ))}
    </ul>
  );
};
